import { type Ref } from "vue";
import instanceJWT from "../../api/apiJwt";
import type { PluginStatus } from "../../models/PluginStatus";
import i18n from "../../../i18n";
import { isExpiredSearchResultsError } from "../../common/helpers/eplex/searchResultsExpired";
import { readSmartFilterSettings, type SmartFilterSettings } from "../../common/helpers/eplex/smartFiltersStorage";

const SEARCH_COMPLETE_HIDE_DELAY_MS = 1000;

/**
 * Перечитывание выдачи под новый фильтр у остановленной проценки.
 *
 * Бэкенд отдаёт выдачу целиком только по ЗАВЕРШЁННОЙ проценке; пока поставщики
 * отвечают, ответ приходит без поля `results`, пустым или с огрызком. При этом проценку
 * бэкенд доводит до конца сам, даже если фронт перестал её опрашивать после «Стоп».
 *
 * Поэтому читаем с повторами до минуты — до ответа, который можно принять
 * (`canApplyResults`). Прежде принимался первый непустой ответ, и жест «влево до упора,
 * потом вправо» оставлял в таблице одну строку вместо всей выдачи: возврат фильтра в
 * «Все» приходил ровно в тот момент, когда бэкенд успел отдать только начало.
 */
const FILTER_READ_RETRY_MS = 2500;
const FILTER_READ_MAX_ATTEMPTS = 24;

interface SearchState {
  loading: boolean;
  results: any[];
  searchStatuses: Record<number, PluginStatus>;
  requestId: string;
  time_from: string;
  isSearching: boolean;
  debugInfo: any;
  progress: number;
  searchToken: number;
  stopped: boolean;
  resultsExpired: boolean;
  /** идёт перечитывание выдачи под новый фильтр — это не поиск */
  filtering: boolean;
  /** какому фильтру соответствует то, что сейчас в results (см. smartFilterKey) */
  resultsFilter: string;
  [key: string]: any;
}

interface PollingContext {
  searches: Record<number, SearchState>;
  pollingActive: Ref<Record<number, boolean>>;
  pollingIntervals: Ref<Record<number, number>>;
  tabActive: Ref<Record<number, boolean>>;
  cacheDeltaForTab: (tabId: number, data: any) => void;
  clearDeltaForTab: (tabId: number) => void;
}

export function updatePluginStatuses(tab: SearchState, pluginsResult: any): Record<number, PluginStatus> {
  const nextStatuses = { ...tab.searchStatuses };
  for (const key in pluginsResult) {
    const plugin = pluginsResult[key];
    const pluginId = Number(key);
    if (pluginId === -1) continue;

    if (plugin.err) {
      nextStatuses[pluginId] = { icon: "error", error: plugin.err };
    } else if (plugin.time_exec) {
      nextStatuses[pluginId] = { icon: "success" };
    } else {
      nextStatuses[pluginId] = { icon: "search" };
    }
  }
  return nextStatuses;
}

export function isSearchComplete(debug: any): boolean {
  const totalSuccess = debug.totalSuccess ?? debug.total_success;
  const totalStarted = debug.totalStarted ?? debug.total_started;
  const totalError = debug.totalError ?? debug.total_error;

  return (
    totalStarted != null && totalSuccess != null && totalError != null && totalSuccess === totalStarted - totalError
  );
}

/**
 * Можно ли принять выдачу из ответа как новое состояние таблицы.
 *
 * Ключевое свойство бэкенда (снято замерами на стенде): выдачу целиком он отдаёт только
 * по ЗАВЕРШЁННОЙ проценке. Пока поставщики отвечают, ответ приходит без поля `results`,
 * пустым или с огрызком — в замере 1 строка там, где после завершения их 6812. Это
 * «пока нечего отдать», а не «вот вся выдача».
 *
 * Отсюда правила:
 *
 * # проценка завершена — ответ авторитетный, принимаем любой, включая пустой (честный
 *   результат фильтра, под который ничего не подошло);
 * # судить нечем (в ответе нет debug-счётчиков) — доверяем ответу;
 * # проценка идёт, а на экране пусто — любые строки лучше пустоты;
 * # проценка идёт, на экране что-то есть — частичным снимком экран не обедняем.
 *   Живой поллинг от этого не страдает: его снимки растут, а не убывают.
 */
/**
 * Ответ окончательный: спорить с ним нечем и перечитывать больше нечего.
 *
 * Это либо завершённая проценка, либо ответ без debug-счётчиков — судить по нему о
 * состоянии поиска мы не можем, значит принимаем как есть.
 */
/**
 * Ключ применённого фильтра. Нужен, чтобы сравнивать выдачу с выдачей ТОГО ЖЕ фильтра:
 * набор «до 3» меньше набора «Все» по существу, а не потому, что бэкенд ещё не собрал
 * результаты, — и запрещать такую «убыль» нельзя.
 */
export function smartFilterKey(settings: any): string {
  if (!settings?.filterApply) return "все";

  const срок = settings.maxTimeEnabled && settings.maxTime < 21 ? settings.maxTime : "";
  const дубли = settings.detailLimitEnabled && settings.detailLimit < 21 ? settings.detailLimit : "";

  return `срок:${срок}|дубли:${дубли}`;
}

export function isAuthoritativeAnswer(data: any): boolean {
  const debug = data?.info?.debug;
  if (!debug || Object.keys(debug).length === 0) return true;

  return isSearchComplete(debug);
}

export function canApplyResults(data: any, options: { currentCount?: number; sameFilter?: boolean } = {}): boolean {
  if (!Array.isArray(data?.results)) return false;

  if (isAuthoritativeAnswer(data)) return true;

  // Проценка идёт — значит ответ может быть неполным.
  //
  // Фильтр сменился: сравнивать не с чем (другой фильтр — другой объём по существу),
  // поэтому показываем первый непустой ответ, а дальше цикл его уточнит.
  if (!options.sameFilter) return data.results.length > 0;

  // Тот же фильтр: частичным снимком экран не обедняем. Живой поллинг от этого не
  // страдает — его снимки растут, а не убывают.
  const currentCount = options.currentCount ?? 0;
  if (currentCount === 0) return data.results.length > 0;

  return data.results.length >= currentCount;
}

export function createPollingActions(ctx: PollingContext) {
  const { searches, pollingActive, pollingIntervals } = ctx;

  const startSequentialPolling = (tabId: number, pollFn: () => Promise<void>, interval: number) => {
    pollingActive.value[tabId] = true;

    const scheduleNext = () => {
      if (!pollingActive.value[tabId]) return;

      pollingIntervals.value[tabId] = window.setTimeout(async () => {
        if (!pollingActive.value[tabId]) return;

        try {
          await pollFn();
        } catch (e) {
          console.error("sequential poll error", e);
        }

        scheduleNext();
      }, interval) as unknown as number;
    };

    scheduleNext();
  };

  const stopPollingForTab = (tabId: number) => {
    pollingActive.value[tabId] = false;
    if (pollingIntervals.value[tabId]) {
      clearTimeout(pollingIntervals.value[tabId]);
      delete pollingIntervals.value[tabId];
    }
  };

  const buildPollParams = (tab: SearchState, smartFiltersSettings: SmartFilterSettings): URLSearchParams => {
    const params = new URLSearchParams();
    params.append("plugin_type", "all");
    if (tab.time_from) {
      params.append("time_from", tab.time_from);
    }

    if (smartFiltersSettings.filterApply) {
      params.append("filter_apply", "true");

      if (smartFiltersSettings.maxTimeEnabled && smartFiltersSettings.maxTime < 21) {
        params.append("max_time", smartFiltersSettings.maxTime.toString());
      }

      if (smartFiltersSettings.detailLimitEnabled && smartFiltersSettings.detailLimit < 21) {
        params.append("detail_limit", smartFiltersSettings.detailLimit.toString());
      }
    }

    return params;
  };

  const stopPollingIfComplete = (tabId: number, debugInfo: any) => {
    if (!isSearchComplete(debugInfo)) return;

    stopPollingForTab(tabId);

    if (searches[tabId]) {
      const currentStatuses = searches[tabId].searchStatuses;
      const finalStatuses: Record<number, PluginStatus> = {};

      Object.keys(currentStatuses).forEach((key) => {
        const pluginId = Number(key);
        const status = currentStatuses[pluginId];
        finalStatuses[pluginId] = status?.error ? { icon: "error", error: status.error } : { icon: "success" };
      });

      searches[tabId].searchStatuses = finalStatuses;
      searches[tabId].loading = false;
      searches[tabId].progress = 100;

      setTimeout(() => {
        if (searches[tabId] && searches[tabId].progress === 100) {
          searches[tabId].isSearching = false;
        }
      }, SEARCH_COMPLETE_HIDE_DELAY_MS);
    }
  };

  function applySearchUpdateToStore(tabId: number, data: any, filterKey = "") {
    if (!ctx.tabActive.value[tabId]) {
      ctx.cacheDeltaForTab(tabId, data);
      return;
    }

    const tab = searches[tabId];
    const updates: Partial<SearchState> = {};

    const pluginsResult = data.info?.debug?.plugins;
    if (pluginsResult) {
      updates.searchStatuses = updatePluginStatuses(tab, pluginsResult);
    }

    // Пустой ответ по идущей проценке — «пока нечего отдать», а не «ничего не найдено»:
    // принимать его нельзя, иначе таблица мигает пустотой посреди поиска
    if (canApplyResults(data, { currentCount: tab.results.length, sameFilter: tab.resultsFilter === filterKey })) {
      const current = tab.results;
      const incoming = data.results;
      const hasChanged =
        incoming.length !== current.length ||
        incoming[incoming.length - 1]?.pos_idx !== current[current.length - 1]?.pos_idx ||
        incoming[incoming.length - 1]?.plugin_id !== current[current.length - 1]?.plugin_id;
      if (hasChanged) {
        updates.results = incoming;
      }
      updates.resultsFilter = filterKey;
    }
    if (data.info?.debug) {
      updates.debugInfo = data.info.debug;

      const debug = data.info.debug;
      const totalStarted = debug.totalStarted ?? debug.total_started ?? 0;
      const totalSuccess = debug.totalSuccess ?? debug.total_success ?? 0;
      const totalError = debug.totalError ?? debug.total_error ?? 0;

      if (totalStarted > 0) {
        updates.progress = Math.min(100, Math.round(((totalSuccess + totalError) / totalStarted) * 100));
      }
    }

    Object.assign(searches[tabId], updates);

    const debug = data.info?.debug;
    if (debug) {
      stopPollingIfComplete(tabId, debug);
    }
  }

  /**
   * Ответ по проценке не получен. `expired` — бэк удалил результаты по сроку
   * хранения (не сбой, а устаревшие данные): вкладка помечается, чтобы таблица
   * объяснила пустоту вместо безликого «Нет данных» (CAB-602 п.4).
   */
  const handlePollError = (tabId: number, expired = false) => {
    stopPollingForTab(tabId);
    if (searches[tabId]) {
      searches[tabId].loading = false;
      searches[tabId].progress = 0;
      searches[tabId].isSearching = false;
      searches[tabId].resultsExpired = expired;
      const errorText = i18n.global.t(expired ? "singleSearch.resultsExpiredStatus" : "errors.searchPollingFailed");
      Object.keys(searches[tabId].searchStatuses).forEach((pluginIdStr) => {
        const pid = Number(pluginIdStr);
        const status = searches[tabId].searchStatuses[pid];
        if (status?.icon === "search") {
          searches[tabId].searchStatuses[pid] = { icon: "error", error: errorText };
        }
      });
    }
  };

  /**
   * Ответ актуален, если вкладка та же, поколение не сменилось (searchToken) и requestId
   * не сменился новым поиском (CAB-586).
   *
   * Отметки `stopped` здесь намеренно нет. Ответы, летевшие до «Стоп», всё равно
   * отбрасываются: `stopSearch` поднимает поколение, а запрос сверяется с тем, что
   * зафиксировал на старте. А вот чтения, начатые ПОСЛЕ «Стоп» осознанно — возврат
   * выдачи после перезагрузки страницы и применение фильтра к найденному, — проверку
   * по `stopped` не проходили, и выдача не доезжала вовсе.
   */
  const isPollStillCurrent = (tabId: number, token: number, requestId: string): boolean => {
    const tab = searches[tabId];
    return !!tab && tab.searchToken === token && tab.requestId === requestId;
  };

  /**
   * Выдача остановленной проценки: обновляем только сами результаты.
   *
   * Статусы поставщиков, прогресс и «идёт поиск» не трогаем — их оставил «Стоп», и
   * перечитывание найденного не должно делать вид, что поиск снова идёт.
   */
  const applyFrozenResults = (tabId: number, data: any, filterKey = ""): boolean => {
    const tab = searches[tabId];
    if (!tab) return false;

    if (data.info?.debug) tab.debugInfo = data.info.debug;

    if (!canApplyResults(data, { currentCount: tab.results.length, sameFilter: tab.resultsFilter === filterKey })) {
      return false;
    }

    tab.results = data.results;
    tab.resultsFilter = filterKey;
    return true;
  };

  const pollOnce = async (tabId: number): Promise<void> => {
    const tab = searches[tabId];
    if (!tab?.requestId) return;

    /**
     * «Стоп» останавливает ОПРОС поставщиков, а не чтение того, что уже найдено.
     *
     * Прежде здесь стоял общий запрет, и он выходил за рамки своей задачи: результаты
     * в сохранённое состояние не пишутся (они большие), после перезагрузки страницы их
     * читают с бэкенда по живому requestId — а для остановленной вкладки чтение не
     * выполнялось, и человек видел пустую таблицу вместо своей выдачи.
     *
     * Цикл опроса для остановленной вкладки по-прежнему не запускается: это решает
     * `updateTabInterval`, а не эта функция.
     */
    const frozen = tab.stopped;

    // Фиксируем идентичность поиска на момент старта запроса — сверим после await
    const token = tab.searchToken;
    const requestId = tab.requestId;

    tab.loading = true;

    // действующие настройки умной фильтрации — с дефолтом для нового пользователя (CAB-630)
    const smartFiltersSettings = readSmartFilterSettings();
    const params = buildPollParams(tab, smartFiltersSettings);
    const url = `eplex/search/result/${encodeURIComponent(requestId)}?${params.toString()}`;

    try {
      const resp = await instanceJWT.get(url);

      // Поиск остановлен или заменён новым, пока запрос летел — ответ устарел, отбрасываем
      if (!isPollStillCurrent(tabId, token, requestId)) return;

      const data = resp.data?.data ?? {};

      if (typeof data.time_from !== "undefined") {
        searches[tabId].time_from = data.time_from;
      }

      if (frozen) {
        applyFrozenResults(tabId, data, smartFilterKey(smartFiltersSettings));
        searches[tabId].loading = false;
        return;
      }

      applySearchUpdateToStore(tabId, data, smartFilterKey(smartFiltersSettings));
    } catch (err) {
      if (!isPollStillCurrent(tabId, token, requestId)) return;
      const expired = isExpiredSearchResultsError(err);
      // истёкшие результаты — ожидаемое состояние старой проценки, не сбой:
      // в консоль сыпать нечего, объяснит таблица
      if (!expired) console.error("pollOnce error for tab", tabId, err);
      handlePollError(tabId, expired);
    }
  };

  const stopSearch = (tabId: number, reason: string = "singleSearch.searchCanceledByUser") => {
    try {
      stopPollingForTab(tabId);
    } catch (e) {
      console.error("Stop search clear interval error: ", e);
    }
    if (searches[tabId]) {
      Object.keys(searches[tabId].searchStatuses).forEach((pluginIdStr) => {
        const pluginId = Number(pluginIdStr);
        const status = searches[tabId].searchStatuses[pluginId];
        if (status?.icon === "search") {
          searches[tabId].searchStatuses[pluginId] = {
            icon: "error",
            error: i18n.global.t(reason),
          };
        }
      });
      searches[tabId].loading = false;
      searches[tabId].progress = 0;
      searches[tabId].isSearching = false;

      // «Стоп» делаем авторитетным (CAB-586). Поиск помечается отменённым (stopped) и
      // инвалидируется поколение (searchToken) — так отбрасываются in-flight ответы, а
      // pollOnce для этой вкладки становится no-op из любого места вызова (переключение
      // вкладок, восстановление, смарт-фильтры). requestId НЕ обнуляем: он нужен корзине
      // (fetchItemInfo/addToCart) и для заморозки уже найденных результатов на экране.
      // Отложенную дельту чистим, чтобы она не влилась при активации вкладки.
      searches[tabId].searchToken += 1;
      searches[tabId].stopped = true;
      ctx.clearDeltaForTab(tabId);
    }
  };

  const buildFilterParams = (tab: SearchState, settings: any): URLSearchParams => {
    const params = new URLSearchParams();
    params.append("plugin_type", "all");
    if (tab.time_from) {
      params.append("time_from", tab.time_from);
    }
    params.append("filter_apply", settings.filterApply.toString());

    if (settings.filterApply) {
      if (settings.maxTimeEnabled && settings.maxTime < 21) {
        params.append("max_time", settings.maxTime.toString());
      }
      if (settings.detailLimitEnabled && settings.detailLimit < 21) {
        params.append("detail_limit", settings.detailLimit.toString());
      }
    }

    return params;
  };

  const processFilteredResults = (tabId: number, data: any, filterKey = ""): void => {
    // Выдачу перезаписываем только когда бэкенд действительно её прислал: прежнее
    // `data.results ?? []` стирало то, что человек уже видел (см. canApplyResults)
    const tab = searches[tabId];
    if (canApplyResults(data, { currentCount: tab.results.length, sameFilter: tab.resultsFilter === filterKey })) {
      tab.results = data.results;
      tab.resultsFilter = filterKey;
    }
    searches[tabId].debugInfo = data.info?.debug ?? {};

    const pluginsResult = data.info?.debug?.plugins;

    if (pluginsResult) {
      searches[tabId].searchStatuses = updatePluginStatuses(searches[tabId], pluginsResult);
      return;
    }

    Object.keys(searches[tabId].searchStatuses).forEach((pluginIdStr) => {
      const pluginId = Number(pluginIdStr);
      searches[tabId].searchStatuses[pluginId] = { icon: "success" };
    });
  };

  const handleFilterPollError = (tabId: number, expired = false) => {
    stopPollingForTab(tabId);

    searches[tabId].resultsExpired = expired;
    const errorText = i18n.global.t(expired ? "singleSearch.resultsExpiredStatus" : "errors.filterApplicationFailed");
    Object.keys(searches[tabId].searchStatuses).forEach((pluginIdStr) => {
      const pluginId = Number(pluginIdStr);
      const status = searches[tabId].searchStatuses[pluginId];
      if (status?.icon === "search") {
        searches[tabId].searchStatuses[pluginId] = { icon: "error", error: errorText };
      }
    });

    searches[tabId].loading = false;
    searches[tabId].isSearching = false;
  };

  const fetchResultsWithFilters = async (tabId: number, settings: any, pollInterval = 3000): Promise<void> => {
    const tab = searches[tabId];
    if (!tab?.requestId) return;

    stopPollingForTab(tabId);
    // Новое поколение: перекрывает live-поллинг прежней сессии (CAB-586)
    tab.searchToken += 1;
    tab.resultsExpired = false;
    // Полная выдача под новый фильтр, а не то, что изменилось с прошлого чтения
    searches[tabId].time_from = "";

    /**
     * Остановленная проценка остаётся остановленной: фильтр перечитывает НАЙДЕННОЕ, а
     * не возобновляет опрос поставщиков. Иначе ползунок «Одинаковых деталей» после
     * «Стоп» снова поднимал бы всем поставщикам «идёт поиск» и запускал цикл опроса —
     * то есть отменял бы «Стоп» без ведома человека.
     */
    const frozen = tab.stopped;

    if (!frozen) {
      tab.stopped = false;
      searches[tabId].isSearching = true;

      const currentStatuses = { ...tab.searchStatuses };
      Object.keys(currentStatuses).forEach((pluginIdStr) => {
        const pluginId = Number(pluginIdStr);
        currentStatuses[pluginId] = { icon: "search" };
      });
      tab.searchStatuses = currentStatuses;
    }

    /**
     * Чтение выдачи под новый фильтр для остановленной проценки.
     *
     * Повторяем до ОКОНЧАТЕЛЬНОГО ответа, а не до первого непустого: промежуточный
     * снимок незавершённой проценки может содержать одну строку там, где после
     * завершения их тысячи. Такой снимок показываем, если он лучше пустого экрана, но
     * чтение на нём не останавливаем — иначе жест «влево до упора, потом вправо»
     * оставлял в таблице одну строку до перезагрузки страницы.
     *
     * Новый жест (новое поколение) ожидание прерывает.
     */
    /**
     * Одна попытка чтения замороженной выдачи: "final" — ответ окончательный,
     * "retry" — промежуточный, "stop" — поиск сменился или запрос упал.
     */
    const readFrozenOnce = async (
      token: number,
      requestId: string,
      url: string,
    ): Promise<"final" | "retry" | "stop"> => {
      try {
        const resp = await instanceJWT.get(url);
        if (!isPollStillCurrent(tabId, token, requestId)) return "stop";

        const data = resp.data?.data ?? {};
        if (typeof data.time_from !== "undefined") searches[tabId].time_from = data.time_from;

        applyFrozenResults(tabId, data, smartFilterKey(settings));
        return isAuthoritativeAnswer(data) ? "final" : "retry";
      } catch (err) {
        if (!isPollStillCurrent(tabId, token, requestId)) return "stop";
        const expired = isExpiredSearchResultsError(err);
        if (!expired) console.error("Failed to fetch results with filters:", err);
        handleFilterPollError(tabId, expired);
        return "stop";
      }
    };

    const readFrozenWithRetries = async (token: number): Promise<void> => {
      const requestId = tab.requestId;
      const params = buildFilterParams(tab, settings);
      const url = `eplex/search/result/${encodeURIComponent(requestId)}?${params.toString()}`;

      for (let attempt = 1; attempt <= FILTER_READ_MAX_ATTEMPTS; attempt++) {
        const outcome = await readFrozenOnce(token, requestId, url);
        if (outcome !== "retry" || attempt === FILTER_READ_MAX_ATTEMPTS) return;
        await new Promise((resolve) => setTimeout(resolve, FILTER_READ_RETRY_MS));
        if (!isPollStillCurrent(tabId, token, requestId)) return;
      }
    };

    // Дошло ли чтение до ОКОНЧАТЕЛЬНОГО ответа. По нему решаем, продолжать ли цикл:
    // статусы поставщиков для этого не годятся — в отфильтрованном ответе они могут
    // выглядеть завершёнными, пока проценка ещё идёт, и тогда цикл прекращался, так и не
    // получив полную выдачу. Со стороны это выглядело так: вернул ползунок в «Все», а в
    // таблице остался набор от «до 3».
    let gotAuthoritativeAnswer = false;

    const pollWithFilters = async (): Promise<void> => {
      const tab = searches[tabId];
      if (!tab?.requestId) return;

      const token = tab.searchToken;
      const requestId = tab.requestId;
      const params = buildFilterParams(tab, settings);
      const url = `eplex/search/result/${encodeURIComponent(requestId)}?${params.toString()}`;

      try {
        const resp = await instanceJWT.get(url);

        // Поиск остановлен или заменён, пока запрос летел — ответ устарел (CAB-586)
        if (!isPollStillCurrent(tabId, token, requestId)) return;

        const data = resp.data?.data ?? {};

        if (typeof data.time_from !== "undefined") {
          searches[tabId].time_from = data.time_from;
        }

        processFilteredResults(tabId, data, smartFilterKey(settings));

        if (isAuthoritativeAnswer(data)) {
          gotAuthoritativeAnswer = true;
          stopPollingForTab(tabId);
          searches[tabId].loading = false;
          searches[tabId].isSearching = false;
        }
      } catch (err) {
        if (!isPollStillCurrent(tabId, token, requestId)) return;
        const expired = isExpiredSearchResultsError(err);
        if (!expired) console.error("Failed to fetch results with filters:", err);
        handleFilterPollError(tabId, expired);
      }
    };

    if (frozen) {
      // Остановленную проценку цикл опроса не подхватывает: только чтение под новый фильтр
      const token = tab.searchToken;
      searches[tabId].filtering = true;
      searches[tabId].loading = true;
      try {
        await readFrozenWithRetries(token);
      } finally {
        // Признаки гасит только своё чтение. Иначе быстрый второй жест выглядел так:
        // прерванное первое чтение снимало подпись «фильтр…», хотя второе ещё работало
        if (searches[tabId]?.searchToken === token) {
          searches[tabId].filtering = false;
          searches[tabId].loading = false;
        }
      }
      return;
    }

    await pollWithFilters();

    // Пока ответ не окончательный, продолжаем перечитывать: полную выдачу под новый
    // фильтр бэкенд отдаст только по завершении проценки
    if (!gotAuthoritativeAnswer && isPollStillCurrent(tabId, tab.searchToken, tab.requestId)) {
      startSequentialPolling(tabId, pollWithFilters, pollInterval);
      return;
    }

    searches[tabId].isSearching = false;
  };

  return {
    startSequentialPolling,
    stopPollingForTab,
    pollOnce,
    stopSearch,
    stopPollingIfComplete,
    applySearchUpdateToStore,
    fetchResultsWithFilters,
  };
}
